// use-credits — lädt den aktuellen Credit-Stand des Users (Topbar-Badge).
// Quelle: GET /credits/balance → { balance: number } (Bearer-Auth).
// Refetch: on-mount + bei `window`-Event 'eve:credits-changed' (nach jedem
// erfolgreichen /v1/chat-Call dispatcht von eve-runtime).

import { useCallback, useEffect, useState } from 'react';
import { ApiError, apiFetch } from '@/lib/api';
import { selectAccessToken, useAuthStore } from '@/stores/auth-store';

/** Event-Name den eve-runtime nach erfolgreichem /v1/chat-Call feuert. */
export const CREDITS_CHANGED_EVENT = 'eve:credits-changed';

interface CreditsBalanceResponse {
  balance: number;
  monthly?: number;
  purchased?: number;
  monthlyAllowance?: number;
}

export interface UseCreditsReturn {
  balance: number | null;
  /** Monats-Credits (Abo-Kontingent), default 0. */
  monthly: number;
  /** Einzeln gekaufte Credits, default 0. */
  purchased: number;
  refresh: () => void;
}

export function useCredits(): UseCreditsReturn {
  const accessToken = useAuthStore(selectAccessToken);
  const [balance, setBalance] = useState<number | null>(null);
  const [monthly, setMonthly] = useState(0);
  const [purchased, setPurchased] = useState(0);

  const refresh = useCallback((): void => {
    if (!accessToken) {
      setBalance(null);
      setMonthly(0);
      setPurchased(0);
      return;
    }
    void (async () => {
      try {
        const data = await apiFetch<CreditsBalanceResponse>('/credits/balance', { accessToken });
        setBalance(typeof data.balance === 'number' ? data.balance : null);
        setMonthly(typeof data.monthly === 'number' ? data.monthly : 0);
        setPurchased(typeof data.purchased === 'number' ? data.purchased : 0);
      } catch (e) {
        const msg = e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'credits load failed';
        console.warn('[credits] balance fetch failed:', msg);
      }
    })();
  }, [accessToken]);

  // Mount + token-change: initial laden.
  useEffect(() => {
    refresh();
  }, [refresh]);

  // Refetch bei Credit-Verbrauch (nach jedem erfolgreichen EVE-Call).
  useEffect(() => {
    const onChanged = (): void => refresh();
    window.addEventListener(CREDITS_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(CREDITS_CHANGED_EVENT, onChanged);
  }, [refresh]);

  return { balance, monthly, purchased, refresh };
}
